import React, { useState, useMemo, useCallback ,useEffect } from 'react';
import '../../../styles/filter.css';
import { DatePicker, Select, Input, Spin, Drawer, Tag } from 'antd';
import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FilterState, Location, Vendor, Prospect, Warehouse, LocationType } from '../../../types';
import { 
  fetchCountries, 
  fetchStates, 
  fetchSalesReps, 
  fetchLocations,
  fetchWarehouses,
  fetchVendors,
  fetchProspects 
} from '../../../services/api';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

const FilterButton = styled(Button)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: '#5f6368',
  padding: '6px 12px',
  borderRadius: '20px',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  background: '#f8f9fa',
  border: 'none',
  textTransform: 'none',
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  height: '36px',

  '&:hover': {
    background: '#f1f3f4',
  },

  '&.active': {
    background: '#e8f0fe',
    color: '#1a73e8',
    fontWeight: 500,
  },
}));

const FilterBadgesContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  '@media (max-width: 700px)': {
    display: 'none'
  }
});

type ReportPeriod =
  | 'all-dates'
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'this-week-to-date'
  | 'last-week'
  | 'last-week-to-date'
  | 'this-month'
  | 'this-month-to-date'
  | 'last-month'
  | 'last-month-to-date'
  | 'this-quarter'
  | 'this-quarter-to-date'
  | 'last-quarter'
  | 'last-quarter-to-date'
  | 'this-year'
  | 'this-year-to-date'
  | 'last-year'
  | 'last-year-to-date'
  | 'this-year-to-last-month'
  | 'recent'
  | 'since-30-days-ago'
  | 'since-60-days-ago'
  | 'since-90-days-ago'
  | 'since-365-days-ago'
  | 'custom';

interface DateRange {
  startDate: string;
  endDate: string;
}

const { RangePicker } = DatePicker;
const DATE_FORMAT = 'YYYY-MM-DD';
const DATE_SUMMARY_FORMAT = 'MMM D, YYYY';
const DEFAULT_DATE_PRESET: ReportPeriod = 'this-year';
const toDateRange = (start: Dayjs, end: Dayjs): DateRange => ({
  startDate: start.format(DATE_FORMAT),
  endDate: end.format(DATE_FORMAT),
});

const getWeekStart = (reference: Dayjs): Dayjs => {
  const startOfDay = reference.startOf('day');
  return startOfDay.subtract(startOfDay.day(), 'day');
};

const getWeekRange = (reference: Dayjs): DateRange => {
  const start = getWeekStart(reference);
  const end = start.add(6, 'day');
  return toDateRange(start, end);
};

const getQuarterStart = (reference: Dayjs): Dayjs => {
  const startOfDay = reference.startOf('day');
  const quarterStartMonth = Math.floor(startOfDay.month() / 3) * 3;
  return startOfDay.set('month', quarterStartMonth).startOf('month');
};

const getQuarterRange = (reference: Dayjs): DateRange => {
  const start = getQuarterStart(reference);
  const end = start.add(1, 'quarter').subtract(1, 'day');
  return toDateRange(start, end);
};

const reportPeriods: Record<ReportPeriod, { label: string; getDateRange: () => DateRange }> = {
  'all-dates': {
    label: 'All Dates',
    getDateRange: () => ({ startDate: '', endDate: '' }),
  },
  today: {
    label: 'Today',
    getDateRange: () => {
      const today = dayjs().startOf('day');
      return toDateRange(today, today);
    },
  },
  yesterday: {
    label: 'Yesterday',
    getDateRange: () => {
      const yesterday = dayjs().subtract(1, 'day').startOf('day');
      return toDateRange(yesterday, yesterday);
    },
  },
  'this-week': {
    label: 'This Week',
    getDateRange: () => getWeekRange(dayjs()),
  },
  'this-week-to-date': {
    label: 'This Week-to-date',
    getDateRange: () => {
      const now = dayjs();
      const start = getWeekStart(now);
      const end = now.startOf('day');
      return toDateRange(start, end);
    },
  },
  'last-week': {
    label: 'Last Week',
    getDateRange: () => getWeekRange(dayjs().subtract(1, 'week')),
  },
  'last-week-to-date': {
    label: 'Last Week-to-date',
    getDateRange: () => {
      const now = dayjs();
      const start = getWeekStart(now).subtract(1, 'week');
      const sameDayOffset = now.day();
      const end = start.add(sameDayOffset, 'day');
      return toDateRange(start, end);
    },
  },
  'this-month': {
    label: 'This Month',
    getDateRange: () => {
      const now = dayjs();
      const start = now.startOf('month');
      const end = now.endOf('month');
      return toDateRange(start, end);
    },
  },
  'this-month-to-date': {
    label: 'This Month-to-date',
    getDateRange: () => {
      const now = dayjs();
      const start = now.startOf('month');
      const end = now.startOf('day');
      return toDateRange(start, end);
    },
  },
  'last-month': {
    label: 'Last Month',
    getDateRange: () => {
      const reference = dayjs().subtract(1, 'month');
      const start = reference.startOf('month');
      const end = reference.endOf('month');
      return toDateRange(start, end);
    },
  },
  'last-month-to-date': {
    label: 'Last Month-to-date',
    getDateRange: () => {
      const now = dayjs();
      const reference = now.subtract(1, 'month');
      const start = reference.startOf('month');
      const daysInLastMonth = reference.daysInMonth();
      const currentDay = now.date();
      const endDay = Math.min(currentDay, daysInLastMonth);
      const end = start.add(endDay - 1, 'day');
      return toDateRange(start, end);
    },
  },
  'this-quarter': {
    label: 'This Quarter',
    getDateRange: () => getQuarterRange(dayjs()),
  },
  'this-quarter-to-date': {
    label: 'This Quarter-to-date',
    getDateRange: () => {
      const now = dayjs();
      const start = getQuarterStart(now);
      const end = now.startOf('day');
      return toDateRange(start, end);
    },
  },
  'last-quarter': {
    label: 'Last Quarter',
    getDateRange: () => getQuarterRange(dayjs().subtract(1, 'quarter')),
  },
  'last-quarter-to-date': {
    label: 'Last Quarter-to-date',
    getDateRange: () => {
      const now = dayjs();
      const currentQuarterStart = getQuarterStart(now);
      const dayOffset = now.startOf('day').diff(currentQuarterStart, 'day');
      const start = getQuarterStart(now.subtract(1, 'quarter'));
      const tentativeEnd = start.add(dayOffset, 'day');
      const lastQuarterEnd = start.add(1, 'quarter').subtract(1, 'day');
      const end = tentativeEnd.isAfter(lastQuarterEnd) ? lastQuarterEnd : tentativeEnd;
      return toDateRange(start, end);
    },
  },
  'this-year': {
    label: 'This Year',
    getDateRange: () => {
      const now = dayjs();
      const start = now.startOf('year');
      const end = now.endOf('year');
      return toDateRange(start, end);
    },
  },
  'this-year-to-date': {
    label: 'This Year-to-date',
    getDateRange: () => {
      const now = dayjs();
      const start = now.startOf('year');
      const end = now.startOf('day');
      return toDateRange(start, end);
    },
  },
  'last-year': {
    label: 'Last Year',
    getDateRange: () => {
      const reference = dayjs().subtract(1, 'year');
      const start = reference.startOf('year');
      const end = reference.endOf('year');
      return toDateRange(start, end);
    },
  },
  'last-year-to-date': {
    label: 'Last Year-to-date',
    getDateRange: () => {
      const now = dayjs();
      const start = now.subtract(1, 'year').startOf('year');
      const end = now.subtract(1, 'year').startOf('day');
      return toDateRange(start, end);
    },
  },
  'this-year-to-last-month': {
    label: 'This Year-to-last-month',
    getDateRange: () => {
      const now = dayjs();
      const start = now.startOf('year');
      const endCandidate = now.subtract(1, 'month').endOf('month');
      const end = endCandidate.isBefore(start) ? start : endCandidate.startOf('day');
      return toDateRange(start, end);
    },
  },
  recent: {
    label: 'Recent',
    getDateRange: () => {
      const now = dayjs().startOf('day');
      const start = now.subtract(30, 'day');
      return toDateRange(start, now);
    },
  },
  'since-30-days-ago': {
    label: 'Since 30 Days Ago',
    getDateRange: () => {
      const now = dayjs().startOf('day');
      const start = now.subtract(30, 'day');
      return toDateRange(start, now);
    },
  },
  'since-60-days-ago': {
    label: 'Since 60 Days Ago',
    getDateRange: () => {
      const now = dayjs().startOf('day');
      const start = now.subtract(60, 'day');
      return toDateRange(start, now);
    },
  },
  'since-90-days-ago': {
    label: 'Since 90 Days Ago',
    getDateRange: () => {
      const now = dayjs().startOf('day');
      const start = now.subtract(90, 'day');
      return toDateRange(start, now);
    },
  },
  'since-365-days-ago': {
    label: 'Since 365 Days Ago',
    getDateRange: () => {
      const now = dayjs().startOf('day');
      const start = now.subtract(365, 'day');
      return toDateRange(start, now);
    },
  },
  custom: {
    label: 'Custom',
    getDateRange: () => ({ startDate: '', endDate: '' }),
  },
};

const calculateDateRange = (period: ReportPeriod): DateRange => {
  const preset = reportPeriods[period];
  if (!preset) {
    throw new Error(`Invalid report period: ${period}`);
  }
  return preset.getDateRange();
};

interface FilterSectionProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({ filters, onFiltersChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [draftFilters, setDraftFilters] = useState<FilterState | null>(null);

  const currentFilters = draftFilters || filters;

  // Initialize draftFilters when drawer opens
  useEffect(() => {
    if (isExpanded && !draftFilters) {
      setDraftFilters({ ...filters });
      // Reset sales rep search when drawer opens
      setSalesRepsSearch('');
      setSalesRepsPage(1);
    }
  }, [isExpanded, filters, draftFilters]);

  // Reset draftFilters when drawer closes
  useEffect(() => {
    if (!isExpanded) {
      setDraftFilters(null);
    }
  }, [isExpanded]);

  // Sync search values with draftFilters
  useEffect(() => {
    if (draftFilters) {
      setCompanySearchValue(draftFilters.searchTerm || '');
      setProductSearchValue(draftFilters.productSearch || '');
      setSelectedDatePreset(draftFilters.dateRangePreset && draftFilters.dateRangePreset in reportPeriods ? draftFilters.dateRangePreset as ReportPeriod : (draftFilters.startDate && draftFilters.endDate ? 'custom' : DEFAULT_DATE_PRESET));
    }
  }, [draftFilters]);

  // Apply draft filters on Apply Filter click
  const handleApplyFilters = useCallback(() => {
    if (draftFilters) {
      onFiltersChange(draftFilters);
    }
    setIsExpanded(false);
  }, [draftFilters, onFiltersChange]);

  // Clear draft filters on Clear Filter click
  const handleClearDraftFilters = useCallback(() => {
    const clearedFilters: FilterState = {
      ...filters,
      country: '',
      state: '',
      city: '',
      searchTerm: '',
      productSearch: '',
      salesRep: '',
      location: '',
      locationType: ['all', 'client', 'warehouse', 'vendor', 'prospect'] as LocationType[],
      startDate: '',
      endDate: '',
      dateRangePreset: 'all-dates',
      mapView: 'standard'
    };
    setDraftFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    setSelectedDatePreset(DEFAULT_DATE_PRESET);
    setCompanySearchValue('');
    setProductSearchValue('');
    setSalesRepsSearch('');
    setSalesRepsPage(1);
    setIsExpanded(false);
  }, [filters, onFiltersChange]);

  // Add spinner animation CSS if not already added
  React.useEffect(() => {
    if (!document.getElementById('custom-spinner-style')) {
      const style = document.createElement('style');
      style.id = 'custom-spinner-style';
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
  const [companySearchValue, setCompanySearchValue] = useState('');
  const [productSearchValue, setProductSearchValue] = useState('');
  const [salesRepsPage, setSalesRepsPage] = useState(1);
  const [salesRepsSearch, setSalesRepsSearch] = useState('');
  const [salesRepsSearchTimeout, setSalesRepsSearchTimeout] = useState<NodeJS.Timeout>();
  const [salesRepsList, setSalesRepsList] = useState<{ staffid: string; alias_name: string }[]>([]);
  const [selectedSalesRepData, setSelectedSalesRepData] = useState<{ staffid: string; alias_name: string } | null>(null);
  const [hasMoreSalesReps, setHasMoreSalesReps] = useState(true);
  const [selectedDatePreset, setSelectedDatePreset] = useState<ReportPeriod>(() => {
    if (filters.dateRangePreset && filters.dateRangePreset in reportPeriods) {
      return filters.dateRangePreset as ReportPeriod;
    }
    if (filters.startDate && filters.endDate) {
      return 'custom';
    }
    return DEFAULT_DATE_PRESET;
  });

  const presetOptions = useMemo(
    () => Object.entries(reportPeriods).map(([key, config]) => ({ value: key, label: config.label })),
    []
  );

  const dateRangeValue = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    const start = currentFilters.startDate ? dayjs(currentFilters.startDate) : null;
    const end = currentFilters.endDate ? dayjs(currentFilters.endDate) : null;
    if (!start || !end) {
      return null;
    }
    return [start, end];
  }, [currentFilters.startDate, currentFilters.endDate]);

  const currentRangeSummary = useMemo(() => {
    if (!filters.startDate && !filters.endDate) {
      return 'All dates';
    }
    const startLabel = filters.startDate ? dayjs(filters.startDate).format(DATE_SUMMARY_FORMAT) : 'Any';
    const endLabel = filters.endDate ? dayjs(filters.endDate).format(DATE_SUMMARY_FORMAT) : 'Any';
    return `${startLabel} to ${endLabel}`;
  }, [filters.startDate, filters.endDate]);

  useEffect(() => {
    if (filters.dateRangePreset && filters.dateRangePreset in reportPeriods) {
      setSelectedDatePreset(filters.dateRangePreset as ReportPeriod);
    } else if (filters.startDate && filters.endDate) {
      setSelectedDatePreset('custom');
    } else {
      setSelectedDatePreset(DEFAULT_DATE_PRESET);
    }
  }, [filters.dateRangePreset, filters.startDate, filters.endDate]);

  const { data: salesRepsData, isLoading: isLoadingSalesReps, isFetching: isFetchingSalesReps } = useQuery({
    queryKey: ['salesReps', salesRepsPage, salesRepsSearch, currentFilters.country, currentFilters.state, currentFilters.city],
    queryFn: () => fetchSalesReps(salesRepsSearch, salesRepsPage),
    enabled: !!(filters.locationType.includes('prospect') || filters.locationType.includes('client')),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: false
  });
  
  // Update salesRepsList when data changes
  useEffect(() => {
    if (salesRepsData) {
      if (salesRepsPage === 1) {
        // Replace the list for first page
        setSalesRepsList(salesRepsData.data);
      } else {
        // Append to the list for subsequent pages
        setSalesRepsList(prev => [...prev, ...salesRepsData.data]);
      }
      
      // Check if there are more pages
      setHasMoreSalesReps(!!salesRepsData.nextPage);
    }
  }, [salesRepsData, salesRepsPage]);
  
  // Handle sales rep search
  const handleSalesRepSearch = (value: string) => {
    setSalesRepsSearch(value);
    
    // Clear existing timeout
    if (salesRepsSearchTimeout) {
      clearTimeout(salesRepsSearchTimeout);
    }
    
    // Reset to first page when search changes
    const timeout = setTimeout(() => {
      setSalesRepsPage(1);
    }, 500);
    
    setSalesRepsSearchTimeout(timeout);
  };
  
  // Handle sales rep scroll
  const handleSalesRepScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    
    // Check if we're near the bottom of the scroll
    if (
      !isLoadingSalesReps && 
      !isFetchingSalesReps && 
      hasMoreSalesReps &&
      target.scrollHeight - target.scrollTop - target.clientHeight < 200
    ) {
      setSalesRepsPage(prev => prev + 1);
    }
  };

  const { data: countries = [], isLoading: isLoadingCountries } = useQuery({
    queryKey: ['countries'],
    queryFn: fetchCountries,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false
  });

  const { data: states = [], isLoading: isLoadingStates } = useQuery({
    queryKey: ['states', currentFilters.country],
    queryFn: () => currentFilters.country ? fetchStates(currentFilters.country) : Promise.resolve([]),
    enabled: !!currentFilters.country,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false
  });

  // Get locations data for counts and cities based on current filters
  const { data: allLocations = { locations: [] }, isLoading: isLoadingLocations } = useQuery({
    queryKey: [
      'count-locations',
      currentFilters.productSearch,
      currentFilters.country,
      currentFilters.state,
      currentFilters.salesRep,
      currentFilters.city,
      currentFilters.location,
      currentFilters.startDate,
      currentFilters.endDate,
      currentFilters.dateRangePreset
    ],
    queryFn: () =>
      fetchLocations(
        currentFilters.productSearch,
        currentFilters.country,
        currentFilters.state,
        currentFilters.salesRep,
        currentFilters.city,
        currentFilters.location,
        currentFilters.startDate,
        currentFilters.endDate
      ),
    staleTime: Infinity
  });

  // Get warehouses data for counts
  const { data: warehouses = [] } = useQuery<Warehouse[]>({
    queryKey: ['warehouses-for-counts', currentFilters.country, currentFilters.state],
    queryFn: () => fetchWarehouses(currentFilters.country, currentFilters.state),
    staleTime: Infinity
  });

  // Get vendors data for counts
  const { data: vendors = [] } = useQuery<Vendor[]>({
    queryKey: [
      'vendors-for-counts',
      currentFilters.productSearch,
      currentFilters.country,
      currentFilters.state,
      currentFilters.city,
      currentFilters.startDate,
      currentFilters.endDate,
      currentFilters.dateRangePreset
    ],
    queryFn: () =>
      fetchVendors(
        currentFilters.productSearch,
        currentFilters.country,
        currentFilters.state,
        currentFilters.city,
        currentFilters.startDate,
        currentFilters.endDate
      ),
    staleTime: Infinity
  });

  // Get prospects data for counts
  const { data: prospects = [] } = useQuery<Prospect[]>({
    queryKey: [
      'prospects-for-counts',
      currentFilters.country,
      currentFilters.state,
      currentFilters.salesRep,
      currentFilters.city,
      currentFilters.productSearch,
      currentFilters.startDate,
      currentFilters.endDate,
      currentFilters.dateRangePreset
    ],
    queryFn: () =>
      fetchProspects(
        currentFilters.country,
        currentFilters.state,
        currentFilters.salesRep,
        currentFilters.city,
        currentFilters.productSearch,
        currentFilters.startDate,
        currentFilters.endDate
      ),
    staleTime: Infinity
  });

  // Loading state for cities
  const isLoadingCities = isLoadingLocations;

  // Calculate location counts across all types for global totals
  const locationCounts = useMemo(() => {
    const counts = {
      countries: {} as Record<string, number>,
      states: {} as Record<string, number>,
      cities: {} as Record<string, number>
    };

    const addCounts = (country?: string, state?: string, city?: string) => {
      if (country) {
        counts.countries[country] = (counts.countries[country] || 0) + 1;
      }
      if (state && (!currentFilters.country || country === currentFilters.country)) {
        counts.states[state] = (counts.states[state] || 0) + 1;
      }
      if (city) {
        counts.cities[city] = (counts.cities[city] || 0) + 1;
      }
    };

    allLocations.locations.forEach((location: Location) => {
      addCounts(location.country?.short_name, location.state, location.city);
    });

    warehouses.forEach((warehouse) => {
      addCounts(warehouse.country?.short_name, warehouse.state, warehouse.city);
    });

    vendors.forEach((vendor) => {
      addCounts(vendor.country?.short_name, vendor.state, vendor.city);
    });

    prospects.forEach((prospect) => {
      addCounts(prospect.country?.short_name, prospect.state, prospect.city);
    });

    return counts;
  }, [allLocations.locations, warehouses, vendors, prospects, currentFilters.country]);

  // Sort options with counts
  const countryOptions = useMemo(() => [
    { key: 'all', value: '', label: 'All Countries' },
    ...countries
      .map(country => ({
        key: country.iso2,
        value: country.short_name,
        label: country.short_name
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
  ], [countries, locationCounts]);

  const stateOptions = useMemo(() => [
    { key: 'all', value: '', label: 'All States' },
    ...states
      .map(state => ({
        key: state,
        value: state,
        label: state
      }))
      .sort((a, b) => a.value.localeCompare(b.value))
  ], [states]);

  // Get cities from all data sources based on selected types
  const cityOptions = useMemo(() => {
    if (!currentFilters.country || !currentFilters.state) return [{ key: 'all', value: '', label: 'All Cities' }];

    const cityCountMap: Record<string, number> = {};

    // Add cities from clients if selected
    if (currentFilters.locationType.includes('client') || currentFilters.locationType.includes('all')) {
      allLocations.locations
        .filter(location =>
          location.country?.short_name === currentFilters.country &&
          location.state === currentFilters.state &&
          location.city
        )
        .forEach(location => {
          cityCountMap[location.city] = (cityCountMap[location.city] || 0) + 1;
        });
    }

    // Add cities from warehouses if selected
    if (currentFilters.locationType.includes('warehouse') || currentFilters.locationType.includes('all')) {
      warehouses
        .filter(warehouse =>
          warehouse.country?.short_name === currentFilters.country &&
          warehouse.state === currentFilters.state &&
          warehouse.city
        )
        .forEach(warehouse => {
          cityCountMap[warehouse.city] = (cityCountMap[warehouse.city] || 0) + 1;
        });
    }

    // Add cities from vendors if selected (with coordinates)
    if (currentFilters.locationType.includes('vendor') || currentFilters.locationType.includes('all')) {
      vendors
        .filter(vendor =>
          vendor.coordinates &&
          vendor.coordinates.length === 2 &&
          vendor.country?.short_name === currentFilters.country &&
          vendor.state === currentFilters.state &&
          vendor.city
        )
        .forEach(vendor => {
          cityCountMap[vendor.city] = (cityCountMap[vendor.city] || 0) + 1;
        });
    }

    // Add cities from prospects if selected
    if (currentFilters.locationType.includes('prospect') || currentFilters.locationType.includes('all')) {
      prospects
        .filter(prospect =>
          prospect.country?.short_name === currentFilters.country &&
          prospect.state === currentFilters.state &&
          prospect.city
        )
        .forEach(prospect => {
          cityCountMap[prospect.city] = (cityCountMap[prospect.city] || 0) + 1;
        });
    }

    // Convert to options array
    const options = Object.keys(cityCountMap)
      .map((city) => ({
        key: city,
        value: city,
        label: city
      }))
      .sort((a, b) => a.value.localeCompare(b.value));

    return [
      { key: 'all', value: '', label: 'All Cities' },
      ...options
    ];
  }, [allLocations.locations, warehouses, vendors, prospects, currentFilters.country, currentFilters.state, currentFilters.locationType]);

  const salesRepOptions = useMemo(() => {
    const options = [
      { key: 'all', value: '', label: 'All Sales Reps' },
      ...salesRepsList.map(rep => ({
        key: rep.staffid,
        value: rep.staffid,
        label: rep.alias_name
      }))
    ];

    // If there's a selected sales rep that's not in the current list, add it
    if (currentFilters.salesRep && selectedSalesRepData && 
        !salesRepsList.find(rep => rep.staffid === currentFilters.salesRep)) {
      options.splice(1, 0, {
        key: selectedSalesRepData.staffid,
        value: selectedSalesRepData.staffid,
        label: selectedSalesRepData.alias_name
      });
    }

    return options;
  }, [salesRepsList, currentFilters.salesRep, selectedSalesRepData]);

  // Memoize handlers to prevent unnecessary re-renders
  const handleCountryChange = useCallback((value: string) => {
    setDraftFilters({
      ...draftFilters!,
      country: value || '',
      state: '',
      city: '',
    });
  }, [draftFilters]);

  const handleStateChange = useCallback((value: string) => {
    setDraftFilters({
      ...draftFilters!,
      state: value || '',
      city: ''
    });
  }, [draftFilters]);

  const handleCityChange = useCallback((value: string) => {
    setDraftFilters({
      ...draftFilters!,
      city: value || ''
    });
  }, [draftFilters]);

  const handleMapViewChange = useCallback((value: 'standard' | 'revenue' | 'sales-pipeline' | 'product' | 'supply-hub' | 'frontsite') => {
    setDraftFilters({
      ...draftFilters!,
      mapView: value
    });
  }, [draftFilters]);

  const handleCompanySearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCompanySearchValue(value);
    setDraftFilters({
      ...draftFilters!,
      searchTerm: value
    });
  }, [draftFilters]);

  const handleProductSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProductSearchValue(value);
    setDraftFilters({
      ...draftFilters!,
      productSearch: value
    });
  }, [draftFilters]);

  const handleDateRangeChange = useCallback((dates: null | [Dayjs | null, Dayjs | null], _dateStrings: [string, string]) => {
    const [start, end] = dates || [];
    if (!start || !end) {
      setSelectedDatePreset(DEFAULT_DATE_PRESET);
      const defaultRange = calculateDateRange(DEFAULT_DATE_PRESET);
      setDraftFilters({
        ...draftFilters!,
        startDate: defaultRange.startDate,
        endDate: defaultRange.endDate,
        dateRangePreset: DEFAULT_DATE_PRESET
      });
      return;
    }

    setSelectedDatePreset('custom');
    setDraftFilters({
      ...draftFilters!,
      startDate: start.format(DATE_FORMAT),
      endDate: end.format(DATE_FORMAT),
      dateRangePreset: 'custom'
    });
  }, [draftFilters]);

  const handleDatePresetChange = useCallback((value: string) => {
    const presetKey = value as ReportPeriod;
    if (!(presetKey in reportPeriods)) {
      setSelectedDatePreset('custom');
      return;
    }

    setSelectedDatePreset(presetKey);

    if (presetKey === 'custom') {
      setSelectedDatePreset('custom');
      return;
    }

    if (presetKey === 'all-dates') {
      setDraftFilters({
        ...draftFilters!,
        startDate: '',
        endDate: '',
        dateRangePreset: 'all-dates'
      });
      return;
    }

    const { startDate, endDate } = calculateDateRange(presetKey);
    setDraftFilters({
      ...draftFilters!,
      startDate,
      endDate,
      dateRangePreset: presetKey
    });
  }, [draftFilters]);

  const handleSalesRepChange = useCallback((value: string) => {
    // Store the selected sales rep data before clearing search
    if (value) {
      const repData = salesRepsList.find(rep => rep.staffid === value);
      if (repData) {
        setSelectedSalesRepData(repData);
      }
    } else {
      setSelectedSalesRepData(null);
    }
    
    // Clear search and reset to first page whenever a selection is made
    setSalesRepsSearch('');
    setSalesRepsPage(1);
    
    setDraftFilters({
      ...draftFilters!,
      salesRep: value
    });
  }, [draftFilters, salesRepsList]);

  const handleReset = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const defaultRange = calculateDateRange(DEFAULT_DATE_PRESET);
    onFiltersChange({
      ...filters,
      city: '',
      searchTerm: '',
      productSearch: '',
      salesRep: '',
      location: '',
      locationType: ['all', 'client', 'warehouse', 'vendor', 'prospect'],
      startDate: defaultRange.startDate,
      endDate: defaultRange.endDate,
      dateRangePreset: DEFAULT_DATE_PRESET
    });
    setSelectedDatePreset(DEFAULT_DATE_PRESET);
    setCompanySearchValue('');
    setProductSearchValue('');
  }, [filters, onFiltersChange]);

  // Create filter badges
  const renderFilterBadges = () => {
    if (!filters.country && !filters.state) return null;

    const badges = [];

    if (filters.country) {
      badges.push(
        <Tag
          key="country"
          color='blue'
          style={{
            height: '40px',
            padding: '4px 12px',
            marginRight: '8px',
            marginBottom: '8px',
            display: 'inline-flex',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <span className='font-medium'>Country: {filters.country}</span>
        </Tag>
      );
    }

    if (filters.state) {
      badges.push(
        <Tag
          key="state"
          color='blue'
          style={{
            height: '40px',
            padding: '4px 12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginRight: '8px',
            marginBottom: '8px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <span className='font-medium'>State: {filters.state}</span>
        </Tag>
      );
    }

    if (filters.city) {
      badges.push(
        <Tag
          key="city"
          color='blue'
          style={{
            height: '40px',
            padding: '4px 12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginRight: '8px',
            marginBottom: '8px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <span className='font-medium'>City: {filters.city}</span>
        </Tag>
      );
    }

    if (filters.mapView) {
      const mapViewLabels = {
        'standard': 'Standard Map',
        'revenue': 'Revenue Map',
        'sales-pipeline': 'Sales Pipeline Map',
        'product': 'Product Map',
        'supply-hub': 'Supply Hub Map',
        'frontsite': 'Frontsite Map'
      } as const;
      badges.push(
        <Tag
          key="map-view"
          color='blue'
          style={{
            height: '40px',
            padding: '4px 12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginRight: '8px',
            marginBottom: '8px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <span className='font-medium'>Map View: {mapViewLabels[filters.mapView]}</span>
        </Tag>
      );
    }

    const shouldShowDateBadge = selectedDatePreset !== DEFAULT_DATE_PRESET || (filters.startDate && filters.endDate);

    if (shouldShowDateBadge) {
      let labelText: string;
      if (selectedDatePreset === 'custom') {
        labelText = filters.startDate && filters.endDate ? currentRangeSummary : 'Custom';
      } else if (selectedDatePreset in reportPeriods) {
        labelText = reportPeriods[selectedDatePreset].label;
      } else {
        labelText = currentRangeSummary;
      }

      badges.push(
        <Tag
          key="date-range"
          color='blue'
          style={{
            height: '40px',
            padding: '4px 12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginRight: '8px',
            marginBottom: '8px',
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <span className='font-medium'>Dates: {labelText}</span>
        </Tag>
      );
    }

    return badges;
  };

  return (
    <>
      <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'end'}}>
        <FilterBadgesContainer>
        {renderFilterBadges()}
        </FilterBadgesContainer>
        <FilterButton
          onClick={(e) => {
            e.stopPropagation();
            handleReset(e);
          }}
          sx={{
            height: '40px',
            minWidth: '40px',
            padding: '8px',
            backgroundColor: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            fontSize: '1rem',
            color: '#222',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            },
            marginRight: '8px',
            marginBottom: '8px',
          }}
        >
          <ReloadOutlined style={{ marginRight: '4px' }}/> Reset
        </FilterButton>
        <FilterButton 
          onClick={() => setIsExpanded(!isExpanded)}
          className={isExpanded ? 'active' : ''}
          sx={{
            height: '40px',
            minWidth: '40px',
            padding: '8px',
            backgroundColor: 'white',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            fontSize: '1rem',
            color: '#222',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            },
            zIndex: 1000,
            marginRight: '8px',
            marginBottom: '8px',
          }}
        >
          <FilterOutlined style={{ marginRight: '4px' }} /> 
            Filter
        </FilterButton>
      </div>

      <Drawer
        title="Filters"
        placement="right"
        onClose={() => setIsExpanded(false)}
        open={isExpanded}
        width={400}
        bodyStyle={{ padding: '16px' }}
        footer={
          <div className="flex gap-2 justify-between">
            <button
              type="button"
              className="cursor-pointer bg-white text-red-500 border border-red-400 px-2 py-1 rounded transition duration-300 hover:bg-red-400 hover:text-white min-w-16"
              onClick={handleClearDraftFilters}
            >
              Clear Filter
            </button>
            <button
              type="button"
              className="cursor-pointer bg-primary px-2 py-1 text-white rounded transition duration-300 hover:bg-primaryLight min-w-16"
              onClick={handleApplyFilters}
            >
              Apply Filter
            </button>
          </div>
        }
      >
        <div className="filter-section-content">
          <div className="filter-group">
            <label>Select Country</label>
            <Select
              value={currentFilters.country}
              onChange={handleCountryChange}
              placeholder="Select Country"
              showSearch
              loading={isLoadingCountries}
              notFoundContent={isLoadingCountries ? <Spin size="small" /> : "No countries found"}
              options={countryOptions}
              filterOption={(input, option) =>
                option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
              }
              optionFilterProp="label"
              maxTagCount={1}
              virtual={false}
            />
          </div>

          <div className="filter-group">
            <label>Select State</label>
            <Select
              value={currentFilters.state}
              onChange={handleStateChange}
              placeholder="Select State"
              disabled={!currentFilters.country || isLoadingStates}
              showSearch
              loading={isLoadingStates}
              notFoundContent={isLoadingStates ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #f3f3f3',
                    borderTop: '2px solid #1890ff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <span style={{ marginLeft: '8px', color: '#666' }}>Loading states...</span>
                </div>
              ) : "No states found"}
              options={stateOptions}
              filterOption={(input, option) =>
                option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
              }
              optionFilterProp="label"
              maxTagCount={1}
              virtual={false}
            />
          </div>

          <div className="filter-group">
            <label>Select City</label>
            <Select
              value={currentFilters.city}
              onChange={handleCityChange}
              placeholder="Select City"
              disabled={!currentFilters.state || isLoadingCities}
              showSearch
              loading={isLoadingCities}
              notFoundContent={isLoadingCities ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' }}>
                  <div style={{ 
                    width: '16px', 
                    height: '16px', 
                    border: '2px solid #f3f3f3', 
                    borderTop: '2px solid #52c41a', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }}></div>
                  <span style={{ marginLeft: '8px', color: '#666' }}>Loading cities...</span>
                </div>
              ) : "No cities found"}
              options={cityOptions}
              filterOption={(input, option) =>
                option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
              }
              optionFilterProp="label"
              maxTagCount={1}
              virtual={false}
            />
          </div>

          <div className="filter-group">
            <label>Date Range</label>
            <Select
              value={selectedDatePreset}
              onChange={handleDatePresetChange}
              options={presetOptions}
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              dropdownMatchSelectWidth
            />
            {selectedDatePreset === 'custom' && (
              <RangePicker
                value={dateRangeValue ?? null}
                onChange={handleDateRangeChange}
                allowClear
                format={DATE_FORMAT}
                style={{ width: '100%', marginTop: 8 }}
              />
            )}
          </div>

          <div className="filter-group">
          <label>Map View</label>
          <Select
            value={currentFilters.mapView || 'standard'}
            onChange={(v) => handleMapViewChange(v as 'standard' | 'revenue' | 'sales-pipeline' | 'product' | 'supply-hub' | 'frontsite')}
            options={[
              { value: 'standard', label: 'Standard Map' },
              { value: 'revenue', label: 'Revenue Map' },
              { value: 'sales-pipeline', label: 'Sales Pipeline Map' },
              { value: 'product', label: 'Product Map' },
              { value: 'supply-hub', label: 'Supply Hub Map' },
              { value: 'frontsite', label: 'Frontsite Map' },
            ]}
          />
        </div>

          <div className="filter-group">
            <label>Search Company</label>
            <Input
              placeholder="Search company..."
              value={companySearchValue}
              onChange={handleCompanySearch}
            />
          </div>

          <div className="filter-group">
            <label>Search Products</label>
            <Input
              placeholder="Search products..."
              value={productSearchValue}
              onChange={handleProductSearch}
            />
          </div>

          {/* <div className="filter-group">
            <label>Location Validation</label>
            <Select
              value={filters.location}
              onChange={(value: string) => onFiltersChange({ ...filters, location: value })}
              placeholder="Select Location Status"
              options={[
                { key: 'all', value: '', label: 'All Locations' },
                { key: 'correct', value: 'correct_location', label: 'Correct Locations' },
                { key: 'incorrect', value: 'incorrect_location', label: 'Incorrect Locations' }
              ]}
            />
          </div> */}

          {(currentFilters.locationType.includes('prospect') || currentFilters.locationType.includes('client')) && (
            <div className="filter-group">
              <label>Select Sales Rep</label>
              <Select
                value={currentFilters.salesRep}
                onChange={handleSalesRepChange}
                placeholder="Select Sales Rep"
                showSearch
                loading={isLoadingSalesReps}
                options={salesRepOptions}
                onSearch={handleSalesRepSearch}
                filterOption={false}
                onPopupScroll={handleSalesRepScroll}
                notFoundContent={isLoadingSalesReps ? <Spin size="small" /> : "No sales reps found"}
                maxTagCount={1}
                virtual={true}
                dropdownRender={(menu) => (
                  <div>
                    {menu}
                    {isFetchingSalesReps && (
                      <div style={{ textAlign: 'center', padding: '8px 0' }}>
                        <Spin size="small" />
                        <span style={{ marginLeft: 8 }}>Loading more...</span>
                      </div>
                    )}
                  </div>
                )}
                dropdownStyle={{
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
                listItemHeight={32}
                listHeight={256}
                style={{ width: '100%' }}
                popupMatchSelectWidth={true}
              />
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default FilterSection;
